import React, { useState, useEffect } from 'react';
import { Wallet } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';

interface SalaryState {
  grossAmount: string;
  country: 'HR' | 'BA' | 'RS';
  children: string;
  hasHealthInsurance: boolean;
  hasDisabilityDeduction: boolean;
}

interface TaxBracket {
  threshold: number;
  rate: number;
}

const countryConfigs = {
  HR: {
    currency: 'EUR',
    symbol: '€',
    baseDeduction: 530.90,
    childDeductions: [70.79, 102.45, 147.69, 192.94], // Per child
    disabilityDeduction: 168.18,
    pensionRate: 0.20,
    healthRate: 0.165,
    taxBrackets: [
      { threshold: 0, rate: 0.20 },
      { threshold: 3359.84, rate: 0.30 }
    ] as TaxBracket[]
  },
  BA: {
    currency: 'BAM',
    symbol: 'KM',
    baseDeduction: 700,
    childDeductions: [100, 100, 100, 100], // Per child
    disabilityDeduction: 200,
    pensionRate: 0.17,
    healthRate: 0.124,
    taxBrackets: [
      { threshold: 0, rate: 0.10 }
    ] as TaxBracket[]
  },
  RS: {
    currency: 'RSD',
    symbol: 'RSD',
    baseDeduction: 21712,
    childDeductions: [4000, 4000, 4000, 4000], // Per child
    disabilityDeduction: 5000,
    pensionRate: 0.14,
    healthRate: 0.102,
    taxBrackets: [
      { threshold: 0, rate: 0.10 }
    ] as TaxBracket[]
  }
};

function KalkulatorPlace() {
  const [values, setValues] = useState<SalaryState>({
    grossAmount: '',
    country: 'HR',
    children: '0',
    hasHealthInsurance: true,
    hasDisabilityDeduction: false
  });

  const [results, setResults] = useState<{
    grossSalary: number;
    pension: number;
    health: number;
    taxBase: number;
    tax: number;
    netSalary: number;
    totalDeductions: number;
  } | null>(null);

  const calculateSalary = () => {
    const config = countryConfigs[values.country];
    const grossSalary = parseFloat(values.grossAmount) || 0;
    
    if (grossSalary <= 0) {
      return null;
    }

    // Calculate mandatory contributions
    const pension = grossSalary * config.pensionRate;
    const health = values.hasHealthInsurance ? grossSalary * config.healthRate : 0;

    // Calculate deductions
    let totalDeduction = config.baseDeduction;
    const numChildren = parseInt(values.children) || 0;
    for (let i = 0; i < Math.min(numChildren, 4); i++) {
      totalDeduction += config.childDeductions[i];
    }
    if (values.hasDisabilityDeduction) {
      totalDeduction += config.disabilityDeduction;
    }

    // Calculate tax base
    const taxBase = Math.max(0, grossSalary - pension - health - totalDeduction);

    // Calculate tax
    let tax = 0;
    const sortedBrackets = [...config.taxBrackets].sort((a, b) => b.threshold - a.threshold);
    let remainingAmount = taxBase;

    for (const bracket of sortedBrackets) {
      if (taxBase > bracket.threshold) {
        const amountInBracket = remainingAmount - bracket.threshold;
        tax += amountInBracket * bracket.rate;
        remainingAmount = bracket.threshold;
      }
    }

    // Calculate net salary
    const netSalary = grossSalary - pension - health - tax;
    const totalDeductions = pension + health + tax;

    return {
      grossSalary,
      pension,
      health,
      taxBase,
      tax,
      netSalary,
      totalDeductions
    };
  };

  const handleInputChange = (field: keyof SalaryState, value: string | boolean) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  useEffect(() => {
    const results = calculateSalary();
    if (results) {
      setResults(results);
    }
  }, [values]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('hr-HR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Kalkulator Plaće",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Izračunajte neto plaću iz bruto iznosa. Uključuje poreze, doprinose i olakšice za Hrvatsku, BiH i Srbiju.",
    "url": "https://online-kalkulator.com/kalkulator-place",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    },
    "featureList": [
      "Izračun neto plaće",
      "Izračun poreza i doprinosa",
      "Podrška za više država",
      "Porezne olakšice",
      "Besplatno korištenje"
    ]
  };

  return (
    <>
      <SEO 
        title="Kalkulator Plaće | Izračun Neto Plaće iz Bruto Iznosa"
        description="Izračunajte neto plaću iz bruto iznosa za Hrvatsku, BiH i Srbiju. Uključuje poreze, doprinose i olakšice. Precizni izračuni za 2025. godinu."
        canonicalUrl="/kalkulator-place"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Wallet className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Kalkulator Plaće</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Input Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bruto plaća
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={values.grossAmount}
                      onChange={(e) => handleInputChange('grossAmount', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={`Unesite bruto iznos u ${countryConfigs[values.country].currency}`}
                    />
                    <span className="absolute right-3 top-2 text-gray-500">
                      {countryConfigs[values.country].symbol}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Država
                  </label>
                  <select
                    value={values.country}
                    onChange={(e) => handleInputChange('country', e.target.value as SalaryState['country'])}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="HR">Hrvatska</option>
                    <option value="BA">Bosna i Hercegovina</option>
                    <option value="RS">Srbija</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Broj djece
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="4"
                    value={values.children}
                    onChange={(e) => handleInputChange('children', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="col-span-2 space-y-2">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={values.hasHealthInsurance}
                      onChange={(e) => handleInputChange('hasHealthInsurance', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Zdravstveno osiguranje</span>
                  </label>

                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={values.hasDisabilityDeduction}
                      onChange={(e) => handleInputChange('hasDisabilityDeduction', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Olakšica za invalidnost</span>
                  </label>
                </div>
              </div>

              {/* Results */}
              {results && (
                <div className="border-t pt-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Bruto plaća:</span>
                    <span className="font-semibold">
                      {formatCurrency(results.grossSalary)} {countryConfigs[values.country].symbol}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Mirovinsko osiguranje:</span>
                    <span className="font-semibold text-red-600">
                      -{formatCurrency(results.pension)} {countryConfigs[values.country].symbol}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Zdravstveno osiguranje:</span>
                    <span className="font-semibold text-red-600">
                      -{formatCurrency(results.health)} {countryConfigs[values.country].symbol}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Porezna osnovica:</span>
                    <span className="font-semibold">
                      {formatCurrency(results.taxBase)} {countryConfigs[values.country].symbol}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Porez:</span>
                    <span className="font-semibold text-red-600">
                      -{formatCurrency(results.tax)} {countryConfigs[values.country].symbol}
                    </span>
                  </div>
                  <div className="flex justify-between items-center pt-3 border-t">
                    <span className="text-gray-800 font-medium">Neto plaća:</span>
                    <span className="text-xl font-bold text-blue-600">
                      {formatCurrency(results.netSalary)} {countryConfigs[values.country].symbol}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-sm text-gray-500">
                    <span>Ukupna davanja:</span>
                    <span>
                      {formatCurrency(results.totalDeductions)} {countryConfigs[values.country].symbol}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Kalkulatoru Plaće</h2>
                <p className="text-gray-600 leading-relaxed">
                  Kalkulator plaće je alat koji vam pomaže izračunati neto plaću iz bruto iznosa,
                  uzimajući u obzir različite doprinose, poreze i olakšice. Podržava izračune za
                  Hrvatsku, Bosnu i Hercegovinu te Srbiju.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Što Uključuje Izračun</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Doprinosi za mirovinsko osiguranje</li>
                  <li>Doprinosi za zdravstveno osiguranje</li>
                  <li>Porez na dohodak prema poreznim razredima</li>
                  <li>Osnovni osobni odbitak</li>
                  <li>Olakšice za uzdržavanu djecu</li>
                  <li>Olakšice za invalidnost</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Unesite bruto iznos plaće</li>
                  <li>Odaberite državu za koju računate plaću</li>
                  <li>Unesite broj uzdržavane djece (ako ih imate)</li>
                  <li>Označite dodatne opcije ako se odnose na vas</li>
                  <li>Rezultati će se automatski izračunati</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Svi izračuni su informativne prirode</li>
                  <li>Iznosi su zaokruženi na dvije decimale</li>
                  <li>Kalkulator koristi važeće stope za 2024. godinu</li>
                  <li>Za točne informacije konzultirajte poreznog savjetnika</li>
                  <li>Izračun se može razlikovati ovisno o specifičnim okolnostima</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Porezne Stope i Doprinosi</h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Hrvatska</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Porez na dohodak: 20% i 30%</li>
                      <li>Mirovinsko osiguranje: 20%</li>
                      <li>Zdravstveno osiguranje: 16.5%</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Bosna i Hercegovina</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Porez na dohodak: 10%</li>
                      <li>Mirovinsko osiguranje: 17%</li>
                      <li>Zdravstveno osiguranje: 12.4%</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Srbija</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Porez na dohodak: 10%</li>
                      <li>Mirovinsko osiguranje: 14%</li>
                      <li>Zdravstveno osiguranje: 10.2%</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Povezani Kalkulatori</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Link 
                    to="/pdv-kalkulator"
                    className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
                  >
                    <h3 className="font-medium text-gray-800 mb-1">PDV Kalkulator</h3>
                    <p className="text-sm text-gray-600">Izračunajte PDV i ukupne iznose</p>
                  </Link>
                  <Link 
                    to="/izracun-mirovine"
                    className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
                  >
                    <h3 className="font-medium text-gray-800 mb-1">Izračun Mirovine</h3>
                    <p className="text-sm text-gray-600">Izračunajte očekivanu mirovinu</p>
                  </Link>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default KalkulatorPlace;